/**
 * Compensarea facturilor FAN cu rambursurile retinute de FAN (13.10.2026). Vezi
 * `modules/fan/compensareFan.ts` in API.
 *
 * Sta sub viramentele FAN, unde se vad zilele fara bani in banca: acolo e intrebarea „unde sunt
 * banii?", iar raspunsul — FAN si-a platit facturile din ei — trebuie sa fie langa.
 */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../../lib/api'
import { useAuthStore } from '../../../stores/auth'
import { Button } from '../../../components/ui/Button'
import { useConfirmare } from '../../../components/ui/Confirmare'
import { Pastila } from '../../../components/ui/Pastila'
import { lei, mesajServer } from './tipuri'

interface StareCompensare {
  pornita: boolean
  ora: string
  ultimaLa: string | null
  ultimulRezultat: {
    trimise?: number
    suma?: number
    ramase?: number
    faraFactura?: number
    eroare?: string
  } | null
}

interface Previzualizare {
  compensari: { zi: string; factura: string; suma: number }[]
  retinut: number
  compensatInainte: number
  faraFactura: number
  facturiDeschise: number
  totalFacturiDeschise: number
  peLuni: { luna: string; n: number; suma: number }[]
}

interface Compensari {
  rows: { id: number; zi: string; factura: string; suma: number; status: string }[]
  trimise: number
  sumaTrimisa: number
}

export function CompensareFan() {
  const qc = useQueryClient()
  const editabil = useAuthStore((s) => s.permissions).includes('fan:post')
  const { confirma } = useConfirmare()
  const [verific, setVerific] = useState(false)
  const [arataLista, setArataLista] = useState(false)

  const { data: status } = useQuery<{ compensareFan?: StareCompensare }>({
    queryKey: ['fan', 'status'],
    queryFn: async () => (await api.get('/fan/status')).data,
  })
  const { data: lista } = useQuery<Compensari>({
    queryKey: ['fan', 'compensari'],
    queryFn: async () => (await api.get('/fan/compensari')).data,
  })
  const stare = status?.compensareFan

  const comuta = useMutation({
    mutationFn: async (pornita: boolean) =>
      (await api.put('/fan/compensare-automata', { pornita })).data as { pornita: boolean },
    onSuccess: (r) => {
      toast.success(
        r.pornita
          ? `Compensarea e pornită. Rulează zilnic la ${stare?.ora ?? ''}.`
          : 'Compensarea automată e oprită.',
      )
      void qc.invalidateQueries({ queryKey: ['fan'] })
    },
    onError: (e: unknown) => toast.error(mesajServer(e, 'Schimbarea n-a reușit.')),
  })

  const trimite = useMutation({
    mutationFn: async () => (await api.post('/fan/compensare/trimite')).data,
    onSuccess: () => {
      toast.success('Am pornit compensarea. Notele pleacă una câte una; lista se actualizează.')
      for (const s of [15, 45, 90, 180, 300]) {
        setTimeout(() => void qc.invalidateQueries({ queryKey: ['fan'] }), s * 1000)
      }
    },
    onError: (e: unknown) => toast.error(mesajServer(e, 'Compensarea n-a pornit.')),
  })

  if (!stare) return null

  const previzualizeaza = async (): Promise<Previzualizare | null> => {
    setVerific(true)
    try {
      return (await api.get('/fan/compensare/previzualizare', { timeout: 4 * 60_000 }))
        .data as Previzualizare
    } catch (e) {
      toast.error(mesajServer(e, 'Nu am putut citi facturile FAN din EA. Nu s-a trimis nimic.'))
      return null
    } finally {
      setVerific(false)
    }
  }

  const intreabaTrimite = async () => {
    const p = await previzualizeaza()
    if (!p) return
    if (p.compensari.length === 0) {
      toast.info(
        p.faraFactura > 0.005
          ? `Nimic de compensat: ${lei(p.faraFactura)} lei reținuți n-au factură FAN deschisă în EA.`
          : 'Nimic de compensat.',
      )
      return
    }
    const total = p.compensari.reduce((t, c) => t + c.suma, 0)
    const ok = await confirma({
      titlu: 'Compensezi facturile FAN în ExpertAccounts?',
      mesaj: (
        <div className="grid gap-2">
          <p>
            Reținut de FAN din rambursuri, de la 01.01.2026: <b>{lei(p.retinut)}</b> lei; deja
            compensat: {lei(p.compensatInainte)} lei.
          </p>
          <p>
            Se trimit <b>{p.compensari.length}</b> note, <b>{lei(total)}</b> lei:{' '}
            <code>401 FAN = 5125.FanCourier</code>, pe facturile FAN de la cea mai veche, cu data
            fiecărei zile.
          </p>
          <ul className="list-disc pl-5 text-sm">
            {p.peLuni.map((l) => (
              <li key={l.luna}>
                {l.luna}: {l.n} note, {lei(l.suma)} lei
              </li>
            ))}
          </ul>
          {p.faraFactura > 0.005 && (
            <p className="text-amber-800">
              {lei(p.faraFactura)} lei reținuți n-au factură FAN deschisă în EA — rămân pe 5125.
            </p>
          )}
          <p>Notele nu se pot anula din CRM.</p>
        </div>
      ),
      buton: `Trimite ${p.compensari.length}`,
      pericol: true,
    })
    if (ok) trimite.mutate()
  }

  const intreabaComuta = async () => {
    const ok = await confirma(
      stare.pornita
        ? { titlu: 'Oprești compensarea automată?', buton: 'Oprește' }
        : {
            titlu: 'Pornești compensarea automată a facturilor FAN?',
            mesaj: `Zilnic la ${stare.ora}, ce a reținut FAN din rambursuri închide facturile FAN deschise în EA, de la cea mai veche, cu data zilei.`,
            buton: 'Pornește',
            pericol: true,
          },
    )
    if (ok) comuta.mutate(!stare.pornita)
  }

  const r = stare.ultimulRezultat
  return (
    <div className="rounded-lg border border-b4l-border bg-white">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-4 py-3 text-sm">
        <span className="font-semibold">Facturi FAN compensate cu rambursuri reținute</span>
        <Pastila ton={stare.pornita ? 'verde' : 'gri'}>
          {stare.pornita ? `Pornită, zilnic la ${stare.ora}` : 'Oprită'}
        </Pastila>
        {lista && (
          <span className="text-xs text-b4l-text-muted">
            {lista.trimise} note trimise, {lei(lista.sumaTrimisa)} lei
          </span>
        )}
        {stare.ultimaLa && r && (
          <span className={`text-xs ${r.eroare ? 'text-b4l-red-dark' : 'text-b4l-text-muted'}`}>
            Ultima rulare ({new Date(stare.ultimaLa).toLocaleString('ro-RO')}):{' '}
            {r.eroare ?? `${r.trimise ?? 0} trimise, ${lei(r.suma ?? 0)} lei`}
          </span>
        )}
        <span className="ml-auto flex flex-wrap gap-2">
          {lista && lista.rows.length > 0 && (
            <Button variant="secondary" onClick={() => setArataLista((x) => !x)}>
              {arataLista ? 'Ascunde lista' : 'Pe facturi'}
            </Button>
          )}
          {editabil && (
            <>
              <Button
                variant="secondary"
                loading={verific || trimite.isPending}
                onClick={() => void intreabaTrimite()}
              >
                {verific ? 'Verific în EA…' : 'Trimite acum'}
              </Button>
              <Button
                variant="secondary"
                loading={comuta.isPending}
                onClick={() => void intreabaComuta()}
              >
                {stare.pornita ? 'Oprește' : 'Pornește'}
              </Button>
            </>
          )}
        </span>
      </div>
      {arataLista && lista && (
        <div className="max-h-80 overflow-auto border-t border-b4l-border px-4 py-2">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-b4l-text-muted">
                <th className="py-1">Zi</th>
                <th>Factura FAN</th>
                <th className="text-right">Sumă</th>
                <th className="pl-3">Stare</th>
              </tr>
            </thead>
            <tbody>
              {lista.rows.map((x) => (
                <tr key={x.id} className="border-t border-b4l-border">
                  <td className="py-1">{new Date(x.zi).toLocaleDateString('ro-RO')}</td>
                  <td>{x.factura}</td>
                  <td className="text-right tabular-nums">{lei(x.suma)}</td>
                  <td className="pl-3">{x.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
